import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import IconButton from '@mui/material/IconButton';
import { styled } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { AvatarCell, avatarToneFromSeed } from '@bloomays-lib/ui.shared/datagrid';
import { initialsFromName } from '@bloomays-lib/utils.shared';
import type { MissionContractBlock } from '../../helpers/legalContracts';

export type MissionGroupHeaderProps = {
  block: MissionContractBlock;
  expanded: boolean;
  onToggleExpanded: () => void;
};

const resolveSocietyName = (name: string | null | undefined, missingMarker: string): string | null => {
  if (!name || name === missingMarker) return null;
  return name;
};

/**
 * Mission block header shown as the row-grouping cell in `TableLegalContracts`
 * (rendered through `muiProps.groupingColDef.renderCell`, since `DataGridV2`'s
 * `guardGroup` only shows the raw grouped value on group rows otherwise).
 *
 * Societies are always shown as **bloomer → client**. Missing societies render
 * a styled warning instead of the raw backend sentinel string
 * (`missing*SocietySentinel` in i18n, injected by `formatMission`).
 */
const MissionGroupHeader = ({ block, expanded, onToggleExpanded }: MissionGroupHeaderProps) => {
  const { t } = useTranslation(['legalContracts']);
  const clientSociety = resolveSocietyName(block.clientSocietyName, t('missingClientSocietySentinel'));
  const bloomerSociety = resolveSocietyName(block.bloomerSocietyName, t('missingBloomerSocietySentinel'));

  return (
    <Root>
      <IconButton
        size="small"
        onClick={(event) => {
          event.stopPropagation();
          onToggleExpanded();
        }}
        aria-label={expanded ? t('collapseMission') : t('expandMission')}
        aria-expanded={expanded}
      >
        {expanded ? <KeyboardArrowDownIcon fontSize="small" /> : <KeyboardArrowRightIcon fontSize="small" />}
      </IconButton>
      <AvatarCell initials={initialsFromName(block.bloomerFullName)} tone={avatarToneFromSeed(block.missionId)} />
      <TextBlock>
        <TitleRow>
          <MissionLabel>{block.missionLabel}</MissionLabel>
          {block.bloomerFullName && (
            <>
              <Separator aria-hidden="true">·</Separator>
              <BloomerName>{block.bloomerFullName}</BloomerName>
            </>
          )}
        </TitleRow>
        <SubtitleRow>
          <SocietiesRow>
            {bloomerSociety ? <span>{bloomerSociety}</span> : <MissingSociety label={t('missingBloomerSociety')} />}
            <ArrowSeparator aria-hidden="true">→</ArrowSeparator>
            {clientSociety ? (
              <ClientSocietyName>{clientSociety}</ClientSocietyName>
            ) : (
              <MissingSociety label={t('missingClientSociety')} />
            )}
          </SocietiesRow>
          {block.accountManagerName ? (
            <>
              <Separator aria-hidden="true">·</Separator>
              <span>
                {t('amPrefix')} {block.accountManagerName}
              </span>
            </>
          ) : null}
        </SubtitleRow>
      </TextBlock>
    </Root>
  );
};

const MissingSociety = ({ label }: { label: string }) => (
  <MissingSocietyLabel>
    <WarningAmberOutlinedIcon sx={{ fontSize: 14 }} aria-hidden />
    {label}
  </MissingSocietyLabel>
);

export default MissionGroupHeader;

const Root = styled('div')(() => ({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  width: '100%',
  minWidth: 0,
}));

const TextBlock = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
  minWidth: 0,
  marginLeft: 4,
}));

const TitleRow = styled('div')(() => ({
  display: 'flex',
  alignItems: 'baseline',
  gap: 6,
  minWidth: 0,
}));

const MissionLabel = styled('span')(({ theme }) => ({
  fontWeight: 600,
  fontSize: 14,
  color: theme.palette.neutral[90],
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
}));

const BloomerName = styled('span')(({ theme }) => ({
  fontSize: 13,
  color: theme.palette.neutral[70],
  whiteSpace: 'nowrap',
}));

const SubtitleRow = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  fontSize: 12,
  color: theme.palette.neutral[60],
  whiteSpace: 'nowrap',
}));

const SocietiesRow = styled('div')(() => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  minWidth: 0,
}));

const ClientSocietyName = styled('span')(() => ({
  fontWeight: 600,
}));

const ArrowSeparator = styled('span')(({ theme }) => ({
  color: theme.palette.neutral[40],
  flexShrink: 0,
}));

const Separator = styled('span')(({ theme }) => ({
  color: theme.palette.neutral[40],
}));

const MissingSocietyLabel = styled('span')(() => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 4,
  color: '#C17A00',
  fontWeight: 600,
}));
